<i18n lang="yaml">
en:
  title: 'Fixed Configurations'
  subtitle: 'Ensuring the infrastructure of&nbsp;any complexity. Create your individual configuration, which you can always modify in&nbsp;the future.'
  blockTitle: 'Create Your Own Cloud Server'
  un: 'un.'
  gb: 'GB'
  pricePerHour: 'Price per hour'
  pricePerMonth: 'Price per month'
  total: 'Total:'
  more: 'More details'
  about: 'on available cloud server configurations'
  moreLink: 'https://kb.selectel.com/docs/selectel-cloud-platform/main-services/cloud-servers/cloud_servers/'
ru:
  title: 'Гибкие конфигурации'
  subtitle: 'Обеспечивают создание инфраструктуры любой сложности. Создайте индивидуальную конфигурацию&nbsp;&mdash; в&nbsp;процессе использования вы&nbsp;всегда можете изменить&nbsp;ее.'
  blockTitle: 'Создайте собственный облачный сервер'
  un: 'шт'
  gb: 'ГБ'
  pricePerHour: 'Цена за час'
  pricePerMonth: 'Цена за месяц'
  total: 'Итого:'
  more: 'Подробнее'
  about: 'про доступные конфигурации облачных серверов'
  moreLink: 'https://kb.selectel.ru/54798198.html'
</i18n>

<template>
  <s-section
    :title="$t('title')"
    :subtitle="$t('subtitle')"
  >
    <s-preloader v-if="isLoading"></s-preloader>
    <template v-else>
      <div
        v-if="!hasMicrodata"
        class="row"
      >
        <div class="col-xl-9 ml-auto">
          <div class="cloud-servers-conf">
            <div class="cloud-servers-conf__wrapper">
              <h3
                class="cloud-servers-conf__title h3"
                v-html="$t('blockTitle')"
              ></h3>

              <div class="cloud-servers-conf__item">
                <div class="cloud-servers-conf__param h3">
                  vCPU
                </div>
                <div class="cloud-servers-conf__slider">
                  <a-slider
                    v-model="vCpuQuantity"
                    :tooltip-visible="true"
                    :tip-formatter="formatterVCpuQuantity"
                    :marks="vCpuMarks"
                    :max="8"
                    :min="1"
                    :step="1"
                    :value="vCpuQuantity"
                  ></a-slider>
                </div>
                <div class="cloud-servers-conf__param">
                  {{ vCpuQuantity }} {{ $t('un') }}
                </div>
              </div>

              <div class="cloud-servers-conf__item">
                <div class="cloud-servers-conf__param h3">
                  RAM
                </div>
                <div class="cloud-servers-conf__slider">
                  <a-slider
                    v-model="ramSize"
                    :tooltip-visible="true"
                    :tip-formatter="formatterRamSize"
                    :marks="ramMarks"
                    :max="64"
                    :min="1"
                    :value="ramSize"
                  ></a-slider>
                </div>
                <div class="cloud-servers-conf__param">
                  {{ ramSize }} {{ $t('gb') }}
                </div>
              </div>

              <div class="cloud-servers-conf__item">
                <div class="cloud-servers-conf__param h3">
                  SSD
                </div>
                <div class="cloud-servers-conf__slider">
                  <a-slider
                    v-model="ssdSize"
                    :tooltip-visible="true"
                    :tip-formatter="formatterSsdSize"
                    :marks="ssdMarks"
                    :max="512"
                    :min="1"
                    :value="ssdSize"
                  ></a-slider>
                </div>
                <div class="cloud-servers-conf__param">
                  {{ ssdSize }} {{ $t('gb') }}
                </div>
              </div>

              <div class="cloud-servers-conf__footer">
                <a-radio-group v-model="currentPeriod">
                  <a-radio-button
                    value="hour"
                  >
                    {{ $t('pricePerHour') }}
                  </a-radio-button>
                  <a-radio-button
                    value="month"
                  >
                    {{ $t('pricePerMonth') }}
                  </a-radio-button>
                </a-radio-group>

                <div class="cloud-servers-conf__total">
                  <span
                    class="cloud-servers-conf__total-title"
                    v-html="$t('total')"
                  ></span>
                  <span class="cloud-servers-conf__total-cost">
                    {{ $priceFormatter(+totalPrice) }}
                  </span>
                  <tax-notification class="cloud-servers-conf__total-tax"></tax-notification>
                </div>
              </div>
            </div>
            <div class="cloud-servers-conf__notice">
              <a
                class="cloud-servers-conf__notice-link"
                :href="$t('moreLink')"
                rel="noopener noreferrer"
                target="_blank"
              >
                {{ $t('more') }}
              </a>
              {{ $t('about') }}
            </div>
          </div>
        </div>
      </div>

      <script
        v-if="hasMicrodata"
        type="application/ld+json"
        v-html="jsonProductMicrodata"
      ></script>
    </template>
  </s-section>
</template>

<script>
import TimeMultiplier from '~/mixins/time-multiplier'
import Microdata from '~/mixins/seo/product-microdata'

import {DAYS_IN_MONTH, HOURS_IN_DAY, KOPECKS_IN_RUBLE} from '~/helpers/constants'

import {Resource} from '~/helpers/prices/vpc/enum'
import {Period} from '~/helpers/enum/period'
import {mapActions, mapGetters} from 'vuex'

export default {
  name: 'CloudServersConfigurator',
  mixins: [
    TimeMultiplier,
    Microdata
  ],
  async fetch() {
    if (this.getVpcCloudServersConfiguratorPrices.length) {
      this.prices = this.getVpcCloudServersConfiguratorPrices
    } else {
      await this.fetchVpcPrices()

      this.prices = this.getVpcCloudServersConfiguratorPrices
    }
    this.isLoading = false
  },
  fetchOnServer: true,
  data() {
    return {
      isLoading: true,
      prices: [],
      vCpuQuantity: 4,
      ramSize: 16,
      ssdSize: 50,
      vCpuMarks: {
        1: 1,
        2: 2,
        3: 3,
        4: 4,
        5: 5,
        6: 6,
        7: 7,
        8: 8
      },
      ramMarks: {
        1: 1,
        16: 16,
        32: 32,
        48: 48,
        64: 64
      },
      ssdMarks: {
        1: 1,
        256: 256,
        512: 512
      },
      currentPeriod: Period.HOUR
    }
  },
  computed: {
    vCpuPrice() {
      const vCpuPrice = this.prices.find(price => price.resource === Resource.COMPUTE_CORES)

      return vCpuPrice
        ? vCpuPrice.value
        : 0
    },
    ramPrice() {
      const ramPrice = this.prices.find(price => price.resource === Resource.COMPUTE_RAM)

      return ramPrice
        ? ramPrice.value
        : 0
    },
    ssdPrice() {
      const ssdPrice = this.prices.find(price => price.resource === Resource.VOLUME_GIGABYTES_LOCAL)

      return ssdPrice
        ? ssdPrice.value
        : 0
    },
    totalPrice() {
      return ((
        this.vCpuQuantity * this.vCpuPrice + this.ramSize * this.ramPrice + this.ssdSize * this.ssdPrice
      ) * this.timeMultiplier / KOPECKS_IN_RUBLE).toFixed(2)
    },
    microdataPrice() {
      return ((this.vCpuPrice + this.ramPrice + this.ssdPrice) *
        DAYS_IN_MONTH * HOURS_IN_DAY / KOPECKS_IN_RUBLE)
        .toFixed(2)
        .toString()
        .replace('.', ',')
    },
    ...mapGetters('rest-client/prices/vpc', ['getVpcCloudServersConfiguratorPrices'])
  },

  methods: {
    setPriceFormat(value, price) {
      return this.$priceFormatter((value * price) * this.timeMultiplier / KOPECKS_IN_RUBLE, 0)
    },
    formatterVCpuQuantity(value) {
      return this.setPriceFormat(value, this.vCpuPrice)
    },
    formatterRamSize(value) {
      return this.setPriceFormat(value, this.ramPrice)
    },
    formatterSsdSize(value) {
      return this.setPriceFormat(value, this.ssdPrice)
    },
    ...mapActions('rest-client/prices/vpc', ['fetchVpcPrices'])
  }
}
</script>

<style lang="scss">
.cloud-servers-conf__wrapper {
  margin-bottom: 24px;
  padding: 32px 24px 48px;
  border: 1px solid $grayBorder;
  border-radius: 6px;

  @media (max-width: $breakpoint-sm) {
    margin-bottom: 18px;
    padding: 24px 16px;
  }
}

.cloud-servers-conf__title {
  margin-bottom: 48px;

  @media (max-width: $breakpoint-sm) {
    margin-bottom: 32px;
  }
}

.cloud-servers-conf__item {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 48px;

  @media (max-width: $breakpoint-lg) {
    align-items: center;
  }
}

.cloud-servers-conf__param {
  min-width: 116px;

  @media (max-width: $breakpoint-lg) {
    min-width: 75px;
    margin-bottom: 14px;
  }

  @media (max-width: $breakpoint-sm) {
    width: 100%;
    margin-bottom: 28px;
  }

  &:last-child {
    text-align: right;

    @media (max-width: $breakpoint-sm) {
      display: none;
    }
  }
}

.cloud-servers-conf__slider {
  flex: 1 0 auto;
  margin-left: 24px;
  margin-right: 24px;

  @media (max-width: $breakpoint-sm) {
    margin-left: 0;
    margin-right: 0;
  }

  .ant-slider-dot {
    display: none;
  }
}

.cloud-servers-conf__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

.cloud-servers-conf__total {
  font-size: 28px;
  color: $secondary;

  @media (max-width: $breakpoint-sm) {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    width: 100%;
    margin-top: 32px;
  }
}

.cloud-servers-conf__total-cost {
  display: inline-block;
  min-width: 150px;
  margin-left: 24px;
  text-align: right;
}

.cloud-servers-conf__total-tax {
  margin-top: 8px;
  color: $primary;
}

.cloud-servers-conf__notice-link {
  display: inline-block;
  color: $secondary;
  border-bottom: 1px solid rgba($secondary, 0.3);
}
</style>